import React from 'react';
import { connect } from 'react-redux';
import { i18n } from 'config/locales';
import { CommonAddressBooks, CommonOnError, CommonOnUpdate } from 'types/props';
import { PhysicalAddressModel } from 'forms/shared/validators/models/physicalAddress/types';
import { getCountryValue, isSafeInternational, isSafePostOffice } from 'helpers/locations/locations';
import { LocationModel } from 'forms/shared/types';
import * as formTypes from 'constants/formTypes';
import Field from '../Field';
import Branch from '../Branch';
import Location from './Location';
import ValidationElement from '../ValidationElement';
import { defaultAlternateAddress } from '../../../schema/form/alternateaddress';
import { setAttribute } from '../shared/setAttribute';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';
import Comments from '../Comments';

const alternateAddressDefaultState = defaultAlternateAddress;

interface Props extends ValidationElementProps {
  attribute?: string
  address?: PhysicalAddressModel
  addressBook?: string
  addressBooks?: CommonAddressBooks
  allowForeignMilitary?: boolean
  belongingTo?: string
  belongingToNA?: string
  country?: string
  layout?: string
  forceAPO?: boolean
  onUpdate?: CommonOnUpdate

  formType: string
  containingSection?: any
  addressFieldMetadata?: any
  onError?: CommonOnError
  required?: boolean
  militaryAddressLabel?: string
  showDNK?: boolean
  AnswerComments?: any
  AddressComments?: any
  PhysicalLocationComments?: any
  foreignMilitaryAddressNegativeMargin?: boolean
}

class AlternateAddress extends ValidationElement<Props> {
  formType: string;

  containingSection: any;

  constructor(props: Props) {
    super(props);

    this.formType = props.formType;
    this.containingSection = props.containingSection;
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.isSameCountry(prevProps.country) && !this.isForeignAddress()) {
      this.props.onUpdate(this.prepareUpdate(alternateAddressDefaultState()));
    }
  }

  prepareUpdate = (toUpdate = {}) => ({
    [this.props.belongingTo]: {
      ...this.props.address,
      ...toUpdate,
    },
  });

  prepareUpdateNotApplicable = (toUpdate = {}) => ({
    [this.props.belongingTo]: {
      ...this.props.address,
      Address: {},
    },
    [this.props.belongingToNA]: {
      ...toUpdate,
    },
  });

  handleUpdate = (values) => {
    this.props.onUpdate(
      this.prepareUpdate({
        Address: values,
      })
    );
  };

  updateAddressNotApplicable = (values) => {
    this.props.onUpdate(
      this.prepareUpdateNotApplicable({
        ...values,
      })
    );
  };

  setAlternateAddress = (values) => {
    if (values.value !== 'Yes') {
      this.props.onUpdate(
        this.prepareUpdate({
          HasDifferentAddress: values,
          Address: {},
        })
      );
    } else {
      this.props.onUpdate(
        this.prepareUpdate({
          HasDifferentAddress: values,
        })
      );
    }
  };

  updateAnswerComments = (values) => {
    this.props.onUpdate({
      AnswerComments: values,
    });
  };

  updateAddressComments = (values) => {
    this.props.onUpdate({
      AddressComments: values,
    });
  };

  updatePhysicalLocationComments = (values) => {
    this.props.onUpdate({
      PhysicalLocationComments: values,
    });
  };

  // XXX figure out how to simplify the ways in which we compute
  // what state the component should render in based on the
  // switches supplied to it
  isForeignMilitaryAddress = () => this.props.address?.HasDifferentAddress?.value === 'Yes' && this.isForeignAddress();

  isForeignAddress = () => {
    if (this.props.forceAPO) { return true; }

    return isSafeInternational(this.props.country);
  };

  isSameCountry = (comparedCountry?: Partial<LocationModel['country']>) => getCountryValue(comparedCountry) === getCountryValue(this.props.country);

  prepareProps = (extraProps = {}) => {
    const defaults = {
      ...this.props.addressFieldMetadata,
      ...this.props.address.Address,
      label: i18n.t('address.label'),
      onUpdate: this.handleUpdate,
      required: true,
      onError: this.props.onError,
    };

    return {
      ...defaults,
      ...extraProps,
    };
  };

  render() {
    const { attribute, foreignMilitaryAddressNegativeMargin } = this.props;
    return (
      <div>
        {this.isForeignAddress() && this.formType !== formTypes.SF852013 && (
          <>
            <Branch
              attribute={setAttribute(attribute, 'HasDifferentAddress')}
              label={this.props.militaryAddressLabel}
              labelSize="h4"
              onUpdate={this.setAlternateAddress}
              value={
                this.props.address.HasDifferentAddress
                  ? this.props.address.HasDifferentAddress.value
                  : ''
              }
              required={this.props.required}
              showDNK={this.props.showDNK}
              warning
            />
            {this.props.AnswerComments && (

              <Comments
                title={i18n.t('relationships.comments.optional')}
                name={`${attribute}AnswerComments`}
                className="optional-comment"
                attribute={`${attribute}AnswerComments`}
                {...this.props.AnswerComments}
                onUpdate={this.props.updateAnswerComments || this.updateAnswerComments}
                required={false}
              />
            )}
          </>
        )}

        {/*
         * This next block is a bit confusing. It renders when the user has indicated
         * that the associated address is in a foreign country AND has selected 'Yes'
         * in the preceeding <Branch/> component, indicating that they (or someone they knew)
         * had an APO/FPO (military) address in the foreign country
         */}
        {this.isForeignMilitaryAddress() && this.formType !== formTypes.SF852013 && (
          <>
            <Field
              title={i18n.t('address.apoFpoRequired')}
              attribute={setAttribute(attribute, 'Address')}
            >
              <Location
                {...this.prepareProps({
                  attribute: setAttribute(attribute, 'Address'),
                  country: 'POSTOFFICE',
                  disableToggle: true,
                  layout: Location.US_ADDRESS,
                  required: this.props.required,
                })}
              />
            </Field>
            {this.props.AddressComments && (
              <Comments
                title={i18n.t('relationships.comments.optional')}
                name={`${attribute}AddressComments`}
                className="optional-comment"
                attribute={`${attribute}AddressComments`}
                {...this.props.AddressComments}
                onUpdate={this.props.updateAddressComments || this.updateAddressComments}
                required={false}
                negativeMarginTop={foreignMilitaryAddressNegativeMargin}
              />
            )}
          </>
        )}

        {!this.props.disablePhysical && isSafePostOffice(this.props.country) && this.formType !== formTypes.SF852013 && (
          <>
            <Field
              title={i18n.t('address.physicalLocationRequired')}
              titleSize="h4"
              attribute={setAttribute(attribute, 'Address')}
            >
              <Location
                {...this.prepareProps({
                  attribute: setAttribute(attribute, 'Address'),
                  addressBook: this.props.addressBook,
                  addressBooks: this.props.addressBooks,
                  geocode: true,
                  forceAPO: this.props.forceAPO,
                  streetLabel: i18n.t('address.physical.street.label'),
                  cityLabel: i18n.t('address.physical.city.label'),
                  layout: this.props.layout,
                  isPoBoxAllowed: false,
                  required: this.props.required,
                })}
              />
            </Field>
            {this.props.PhysicalLocationComments && (
              <Comments
                title={i18n.t('relationships.comments.optional')}
                name={`${attribute}PhysicalLocationComments`}
                className="optional-comment"
                attribute={setAttribute(attribute, 'PhysicalLocationComments')}
                {...this.props.PhysicalLocationComments}
                onUpdate={this.props.updatePhysicalLocationComments || this.updatePhysicalLocationComments}
                required={false}
                negativeMarginTop={false}
              />
            )}
          </>
        )}
      </div>
    );
  }
}

AlternateAddress.defaultProps = {
  address: {
    HasDifferentAddress: {
      value: '',
    },
  },
  addressBook: 'Residence',
  addressFieldMetadata: {
    streetLabel: i18n.t('address.us.street.label'),
    street2Label: i18n.t('address.us.street2.label'),
    stateLabel: i18n.t('address.us.state.label'),
    cityLabel: i18n.t('address.us.city.label'),
    zipcodeLabel: i18n.t('address.us.zipcode.label'),
    countyLabel: i18n.t('address.us.county.label'),
    countryLabel: i18n.t('address.international.country.label'),
  },
  forceAPO: false,
  layout: Location.ADDRESS,
  militaryAddressLabel: i18n.t('address.militaryAddress.meEmployment'),
  // defaultProp for country is to minimize regressions associated to new
  // entries. The default value for this should be United States and
  // AlternativeAddress is the first "Location"-type component that some
  // subsections see, however, the value is not defined.
  country: { value: 'United States' },
  foreignMilitaryAddressNegativeMargin: true,
};

const mapStateToProps = ({ application }, ownProps) => {
  const containingSection = ownProps.containingSection || ' ';
  const address = ownProps.address || alternateAddressDefaultState();
  const formType = application.Settings.formType;
  return {
    address,
    addressBooks: application.AddressBooks,
    formType,
    containingSection,
  };
};

export { AlternateAddress };
export default connect(mapStateToProps)(AlternateAddress);
